import Link from "next/link";
import { Icon } from "@iconify/react";

type PricingSectionProps = {
  showHeader?: boolean;
};

const PLANS = [
  {
    key: "starter",
    name: "Starter",
    price: 20,
    blurb: "For small teams getting a workspace up and running.",
    featured: true,
    badge: "Start here",
    features: [
      "Up to 3 owned workspaces",
      "10,000 shared monthly credits",
      "Overage up to 2,000 credits",
      "Unlimited team members",
      "14-day free trial",
    ],
  },
  {
    key: "business",
    name: "Business",
    price: 49,
    blurb: "For teams running more projects across departments.",
    featured: false,
    features: [
      "Up to 10 owned workspaces",
      "30,000 shared monthly credits",
      "Overage up to 5,000 credits",
      "Unlimited team members",
    ],
  },
  {
    key: "agency",
    name: "Agency",
    price: 99,
    blurb: "For agencies and orgs managing many client workspaces.",
    featured: false,
    features: [
      "Up to 30 owned workspaces",
      "75,000 shared monthly credits",
      "Overage up to 10,000 credits",
      "Unlimited team members",
    ],
  },
] as const;

export function PricingSection({ showHeader = true }: PricingSectionProps) {
  return (
    <section id="pricing" className="border-t border-[#2B3624]/[0.06] py-14 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-6xl">
        {showHeader && (
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-xs font-medium uppercase tracking-widest text-[#7C8D52]">
              Simple workspace pricing
            </span>
            <h2 className="mt-4 text-3xl font-normal tracking-tight text-[#2B3624] sm:text-4xl lg:text-5xl">
              Pay for workspaces, not every person.
            </h2>
            <p className="mt-5 text-base text-[#66705F] sm:text-lg">
              Invite members at no extra seat cost. Start with a 14-day free trial — no card required.
            </p>
          </div>
        )}

        <div className={`grid items-stretch gap-6 lg:grid-cols-3 ${showHeader ? "mt-14" : ""}`}>
          {PLANS.map((plan) =>
            plan.featured ? (
              <article
                key={plan.key}
                className="relative flex flex-col rounded-2xl border border-[#7C8D52] bg-[#2B3624] p-7 text-[#F6F3EC] shadow-[0_30px_80px_-35px_rgba(43,54,36,0.7)]"
              >
                {"badge" in plan && plan.badge ? (
                  <span className="absolute right-5 top-5 rounded-full bg-[#C9D68A] px-3 py-1 text-xs font-medium text-[#1D2718]">
                    {plan.badge}
                  </span>
                ) : null}
                <span className="max-w-[calc(100%-6rem)] text-xs font-medium uppercase tracking-widest text-[#C9D68A]">
                  {plan.name}
                </span>
                <div className="mt-5 flex items-end gap-2">
                  <span className="text-5xl font-medium tracking-tight">${plan.price}</span>
                  <span className="pb-1 text-sm text-[#D8D0B8]">/month</span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-[#D8D0B8]">{plan.blurb}</p>
                <ul className="mt-8 flex-1 space-y-3 text-sm text-[#E7E2D3]">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <Icon icon="solar:check-circle-linear" className="mt-0.5 shrink-0 text-[#C9D68A]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  href="/signup"
                  className="mt-10 inline-flex w-full items-center justify-center rounded-md bg-[#C9D68A] px-4 py-2.5 text-sm font-medium text-[#1D2718] transition-colors hover:bg-[#B8C57A]"
                >
                  Start free trial
                </Link>
              </article>
            ) : (
              <article
                key={plan.key}
                className="flex flex-col rounded-2xl border border-[#2B3624]/10 bg-[#EBE6DA] p-7"
              >
                <span className="text-xs font-medium uppercase tracking-widest text-[#7B8277]">
                  {plan.name}
                </span>
                <div className="mt-5 flex items-end gap-2 text-[#2B3624]">
                  <span className="text-5xl font-medium tracking-tight">${plan.price}</span>
                  <span className="pb-1 text-sm text-[#8A9185]">/month</span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-[#66705F]">{plan.blurb}</p>
                <ul className="mt-8 flex-1 space-y-3 text-sm text-[#5F675B]">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-2">
                      <Icon icon="solar:check-circle-linear" className="mt-0.5 shrink-0 text-[#7C8D52]" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  href="/signup"
                  className="mt-10 inline-flex w-full items-center justify-center rounded-md border border-[#2B3624]/15 px-4 py-2.5 text-sm font-medium text-[#2B3624] transition-colors hover:bg-white/50"
                >
                  Get started
                </Link>
              </article>
            ),
          )}
        </div>

        <p className="mt-10 text-center text-sm text-[#66705F]">
          Already invited?{" "}
          <Link href="/login" className="font-medium text-[#2B3624] underline-offset-2 hover:underline">
            Sign in to join a workspace
          </Link>{" "}
          — no plan required.
        </p>
      </div>
    </section>
  );
}

export function MarketingFooter() {
  return (
    <footer className="border-t border-[#2B3624]/[0.06] py-12">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-6 sm:flex-row">
        <p className="text-sm text-[#66705F]">© {new Date().getFullYear()} CollaboratorX.ai</p>
        <div className="flex items-center gap-6 text-sm font-medium text-[#5F675B]">
          <Link href="/login" className="transition-colors hover:text-[#2B3624]">
            Sign in
          </Link>
          <Link href="/signup" className="transition-colors hover:text-[#2B3624]">
            Start free
          </Link>
          <Link href="/pricing" className="transition-colors hover:text-[#2B3624]">
            Pricing
          </Link>
        </div>
      </div>
    </footer>
  );
}
